import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import './ThemedSelect.css';
import './ThemedDropdown.css';

export interface ThemedSelectOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
  action?: boolean;
}

interface ThemedSelectProps<T extends string> {
  value: T;
  options: ThemedSelectOption<T>[];
  onChange: (value: T) => void;
  ariaLabel: string;
  ariaDescribedBy?: string;
  disabled?: boolean;
  className?: string;
  invalid?: boolean;
}

const ThemedSelect = <T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  ariaDescribedBy,
  disabled = false,
  className = '',
  invalid = false,
}: ThemedSelectProps<T>) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((option) => option.value === value) ?? options[0];

  useEffect(() => {
    const close = (event: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, []);

  return (
    <div className={`themed-select ${invalid ? 'themed-select-invalid' : ''} ${className}`.trim()} ref={ref}>
      <button
        type="button"
        className="themed-select-trigger"
        onClick={() => setOpen((current) => !current)}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-expanded={open}
        aria-invalid={invalid}
        data-open={open || undefined}
        disabled={disabled}
      >
        <span>{selected?.label}</span>
        <ChevronDown className={open ? 'open' : ''} size={18} aria-hidden />
      </button>
      {open && (
        <div className="themed-select-options themed-dropdown-menu" role="listbox">
          {options.map((option) => (
            <button
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={`themed-select-option themed-dropdown-option ${option.value === value ? 'themed-dropdown-option-active' : ''}${option.action ? ' themed-select-option-action' : ''}`}
              key={option.value}
              disabled={option.disabled}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              {option.action && <Plus size={17} aria-hidden />}
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ThemedSelect;
